import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { EventsService } from '..';
import { SERVER_DISCONNECTION } from 'src/app/_helpers';
import * as moment from 'moment';
import { ConfigurationService } from '../configuration/configuration.service';
import { ConfigurationKeys } from 'src/app/app.structs';
import { ConnectivityMap } from './connectivity.interface';
import { DATE_TIME_FORMAT_UTILITY_IV } from 'sharedweb/helpers/shared-constants';
import { MockTradingSession } from 'src/app/app.interfaces';

@Injectable({
  providedIn: 'root'
})
/**
 * Service for managing connectivity status.
 */
export class ConnectivityService {

  connectivityStatusMap: ConnectivityMap = {
    goServerDisConnected: false,
    webServerDisConnected: false,
    mdlDisConnected: false,
    isUnderBODTime: false,
    hftDMSDisConnected: false,
    mockTradingSession: false,
  };
  connectivitySnapshots = new BehaviorSubject<ConnectivityMap>(this.connectivityStatusMap);

  private _authSubscriptions = new Subscription();
  private _alwaysAliveSubscriptions = new Subscription();
  currentMockSession: MockTradingSession;

  private intervalId = null;

  private _sendConnectivitySnapshot = () => { 
    this.connectivitySnapshots.next(this.connectivityStatusMap);
  }

  constructor(
    private _eventService: EventsService,
    private _remoteConfigService: ConfigurationService
  ) { 
  }

  setAuthSubscriptions() {
    this._authSubscriptions.add(
      this._eventService.subscribe(SERVER_DISCONNECTION.MDL, (mdlDisConnected: boolean) => {
        if ( this.connectivityStatusMap.mdlDisConnected !== mdlDisConnected) {
          this.connectivityStatusMap.mdlDisConnected = mdlDisConnected;
          this._sendConnectivitySnapshot();
        }
      })
    );

    this._authSubscriptions.add( 
      this._eventService.subscribe(SERVER_DISCONNECTION.GOSERVER, (goServerDisConnected: boolean) => {
        if ( this.connectivityStatusMap.goServerDisConnected !== goServerDisConnected) { 
          this.connectivityStatusMap.goServerDisConnected = goServerDisConnected;
          this._sendConnectivitySnapshot();
        }
      })
     );
     
     this.startCheckBODTimer();
  }

  
  setAlwaysAliveSubscriptions() {
    this._alwaysAliveSubscriptions.add(
      this._eventService.subscribe(SERVER_DISCONNECTION.SNAPSHOTS, (connectivityStatus: ConnectivityMap) => {
          this.connectivityStatusMap = { ...this.connectivityStatusMap, ...connectivityStatus };
          this._sendConnectivitySnapshot();
      })
    );


    this._alwaysAliveSubscriptions.add(
      this._eventService.subscribe(SERVER_DISCONNECTION.HFTDMS, (hftDmsDisConnected: boolean) => {
        if ( this.connectivityStatusMap.hftDMSDisConnected !== hftDmsDisConnected) { 
          this.connectivityStatusMap.hftDMSDisConnected = hftDmsDisConnected;
          this._sendConnectivitySnapshot();
        }
      })
     );
    

     this._alwaysAliveSubscriptions.add( 
      this._eventService.subscribe(SERVER_DISCONNECTION.WEBSERVER, (webServerDisConnected: boolean) => {
        if ( this.connectivityStatusMap.webServerDisConnected !== webServerDisConnected) { 
          this.connectivityStatusMap.webServerDisConnected = webServerDisConnected;
          this._sendConnectivitySnapshot();
        }
      })
     );
  }

  /**
   * Starts the timer for checking the current time and updating the BOD time status.
   * If the current time is within the BOD time range (HH:MM to HH:MM), it sets the BOD time status to true.
   * The timer runs every second to continuously check the current time and update the BOD time status accordingly.
   * If the current time is outside the BOD time range, it sets the BOD time status to false and stops the timer.
   */
  startCheckBODTimer() {
    const {bodEnabled, bodStartTime, bodEndTime} = this._remoteConfigService.remoteConfigCacheObj.BOD_TIME_NUDGE;

    if (!this.intervalId && bodEnabled && bodStartTime && bodEndTime) {
      try {
        this.setTimer(bodStartTime, bodEndTime);
      }  catch (error) { 
        console.error('Error in startCheckBODTimer', error);
        clearInterval(this.intervalId);
        this.intervalId = null;
        //retry on error.
        this.setTimer(bodStartTime, bodEndTime);
      }
    }
  }

  setTimer(bodStartTime: string, bodEndTime: string) {
    this.intervalId = setInterval(() => {
      const currentTime = moment();
      const isUnderBodTime = currentTime.isAfter(moment(bodStartTime, 'HH:mm A')) && currentTime.isBefore(moment(bodEndTime, 'HH:mm A'));
      if (this.connectivityStatusMap.isUnderBODTime !== isUnderBodTime) {
        this.connectivityStatusMap.isUnderBODTime = isUnderBodTime;
        this._sendConnectivitySnapshot();
      }
    }, 1000);
  }

  /**
   * Checks if the current date is within the mock trading session dates.
   * If it is, it sets the mock trading session status to true.
   * If it is not, it sets the mock trading session status to false.
   */
  checkMockTradingSession() {
    const mockTradingConfig = this._remoteConfigService.remoteConfigCacheObj.MOCK_SESSION_NUDGE;
    if (mockTradingConfig) {
      const today = moment().format(DATE_TIME_FORMAT_UTILITY_IV);
      const todaySession = mockTradingConfig[today];

      const showMessage = today in mockTradingConfig;
      if (this.connectivityStatusMap.mockTradingSession !== showMessage) {
        this.connectivityStatusMap.mockTradingSession = showMessage;
        this.currentMockSession = todaySession;
        this._sendConnectivitySnapshot();
      }
    }
  }

  clearAuthSubscriptions() {
    clearInterval(this.intervalId);
    this.intervalId = null;
    this._authSubscriptions.unsubscribe();
  }

}
